<template>
    <div class="countdown-widget">
        <div class="widget-header">
            <div class="left">
                <i
                    class="material-icons drag-handle"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                        arrow: true,
                        animation: 'scale-extreme',
                        duration: [200, 200],
                        hideOnClick: true,
                    }"
                    content="Sürükleyerek taşıyabilirsiniz"
                    >drag_indicator</i
                >
                <widget-title :widget="widget" :table="table" @widget-updated="handleWidgetUpdated" />
            </div>

            <widget-actions
                @fullscreen="handleFullscreen"
                @edit="handleEdit"
                @rename="handleRename"
                @duplicate="handleDuplicate"
                @dock="handleDock"
                @export="handleExport"
                @delete="$emit('delete', widget._id)"
                :title="widget.title"
                :widget="widget"
                :table="table"
                @widget-updated="handleWidgetUpdated"
                @widget-duplicated="handleWidgetDuplicated"
                @widget-deleted="handleWidgetDeleted"
            />
        </div>
        <div 
            class="widget-content" 
            :style="{
                backgroundColor: $_.get(widget, 'settings.style.backgroundColor', '#ffffff'),
                color: $_.get(widget, 'settings.style.textColor', '#000000')
            }"
        >
            <empty-widget
                v-if="!hasValidSettings"
                :error="null"
                :has-valid-settings="hasValidSettings"
                @edit="handleEdit"
            />
            <div v-else class="countdown-content">
                <div 
                    v-if="$_.get(widget, 'settings.showUnits.months', false)" 
                    class="countdown-unit"
                    :style="{ fontSize: $_.get(widget, 'settings.style.fontSize', 48) + 'px' }"
                >
                    <div class="value">{{ timeLeft.months }}</div>
                    <div class="label" :style="{ fontSize: $_.get(widget, 'settings.style.labelFontSize', 16) + 'px' }">
                        {{ $t('table.months') }}
                    </div>
                </div>
                <div 
                    v-if="$_.get(widget, 'settings.showUnits.weeks', false)" 
                    class="countdown-unit"
                    :style="{ fontSize: $_.get(widget, 'settings.style.fontSize', 48) + 'px' }"
                >
                    <div class="value">{{ timeLeft.weeks }}</div>
                    <div class="label" :style="{ fontSize: $_.get(widget, 'settings.style.labelFontSize', 16) + 'px' }">
                        {{ $t('table.weeks') }}
                    </div>
                </div>
                <div 
                    v-if="$_.get(widget, 'settings.showUnits.days', true)" 
                    class="countdown-unit"
                    :style="{ fontSize: $_.get(widget, 'settings.style.fontSize', 48) + 'px' }"
                >
                    <div class="value">{{ timeLeft.days }}</div>
                    <div class="label" :style="{ fontSize: $_.get(widget, 'settings.style.labelFontSize', 16) + 'px' }">
                        {{ $t('table.days') }}
                    </div>
                </div>
                <div 
                    v-if="$_.get(widget, 'settings.showUnits.hours', true)" 
                    class="countdown-unit"
                    :style="{ fontSize: $_.get(widget, 'settings.style.fontSize', 48) + 'px' }"
                >
                    <div class="value">{{ timeLeft.hours }}</div>
                    <div class="label" :style="{ fontSize: $_.get(widget, 'settings.style.labelFontSize', 16) + 'px' }">
                        {{ $t('table.hours') }}
                    </div>
                </div>
                <div 
                    v-if="$_.get(widget, 'settings.showUnits.minutes', true)" 
                    class="countdown-unit"
                    :style="{ fontSize: $_.get(widget, 'settings.style.fontSize', 48) + 'px' }"
                >
                    <div class="value">{{ timeLeft.minutes }}</div>
                    <div class="label" :style="{ fontSize: $_.get(widget, 'settings.style.labelFontSize', 16) + 'px' }">
                        {{ $t('table.minutes') }}
                    </div>
                </div>
                <div 
                    v-if="$_.get(widget, 'settings.showUnits.seconds', true)" 
                    class="countdown-unit"
                    :style="{ fontSize: $_.get(widget, 'settings.style.fontSize', 48) + 'px' }"
                >
                    <div class="value">{{ timeLeft.seconds }}</div>
                    <div class="label" :style="{ fontSize: $_.get(widget, 'settings.style.labelFontSize', 16) + 'px' }">
                        {{ $t('table.seconds') }}
                    </div>
                </div>
            </div>
        </div>

        <countdown-settings-modal
            v-if="showSettingsModal"
            :visible="showSettingsModal"
            :widget="widget"
            @close="showSettingsModal = false"
            @save="handleSettingsSave"
            :table="table"
        />
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../../api/table";
import log2 from "../../../../../../utils/log2";
import WidgetActions from "../charts/components/WidgetActions.vue";
import WidgetTitle from "../charts/components/WidgetTitle.vue";
import EmptyWidget from "../EmptyWidget.vue";
import CountdownSettingsModal from "./CountdownSettingsModal.vue";

const log = log2("CountdownWidget");

export default Vue.extend({
    name: "CountdownWidget",
    components: {
        WidgetActions,
        WidgetTitle,
        EmptyWidget,
        CountdownSettingsModal,
    },
    props: {
        widget: {
            type: Object,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showSettingsModal: false,
            timeLeft: {
                months: 0,
                weeks: 0,
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
            },
            timer: null,
        };
    },
    computed: {
        hasValidSettings() {
            return !!this.$_.get(this.widget, 'settings.targetDate');
        },
    },
    mounted() {
        this.startTimer();
    },
    beforeDestroy() {
        if (this.timer) {
            clearInterval(this.timer);
        }
    },
    methods: {
        startTimer() {
            if (!this.hasValidSettings) return;

            this.updateTimeLeft();
            this.timer = setInterval(() => {
                this.updateTimeLeft();
            }, 1000);
        },
        updateTimeLeft() {
            const now = this.$moment();
            const targetDate = this.$moment(this.$_.get(this.widget, 'settings.targetDate'));
            const duration = this.$moment.duration(targetDate.diff(now));

            if (duration.asSeconds() <= 0) {
                this.timeLeft = {
                    months: 0,
                    weeks: 0,
                    days: 0,
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                };
                clearInterval(this.timer);
                return;
            }

            this.timeLeft = {
                months: Math.floor(duration.asMonths()),
                weeks: Math.floor(duration.asWeeks() % 4),
                days: Math.floor(duration.asDays() % 7),
                hours: duration.hours(),
                minutes: duration.minutes(),
                seconds: duration.seconds(),
            };
        },
        handleFullscreen() {
            // TODO: Tam ekran modunu implement et
        },
        async handleRename(newName) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    title: newName,
                });
                this.$emit("widget-updated", {
                    ...this.widget,
                    title: newName,
                });
            } catch (error) {
                console.error("Widget adı güncellenirken hata:", error);
                throw error;
            }
        },
        async handleDuplicate() {
            try {
                const result = await TABLE_API.duplicateWidget(this.table._id, this.widget.page, this.widget._id);
                this.$message.success("Widget başarıyla çoğaltıldı");
                this.$emit("widget-duplicated", {
                    widget: result.widget,
                    page: result.page,
                });
            } catch (error) {
                console.error("Widget çoğaltılırken hata:", error);
                this.$message.error("Widget çoğaltılırken bir hata oluştu");
            }
        },
        handleDock() {
            // TODO: Sabitleme işlemini implement et
        },
        handleExport() {
            // TODO: Dışa aktarma işlemini implement et
        },
        handleEdit() {
            this.showSettingsModal = true;
        },
        async handleSettingsSave(newSettings) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    settings: newSettings,
                });
                this.$message.success("Widget ayarları güncellendi");
                this.$emit("widget-updated", {
                    ...this.widget,
                    settings: newSettings,
                });
                this.showSettingsModal = false;
                this.startTimer(); // Timer'ı yeni ayarlarla yeniden başlat
            } catch (error) {
                console.error("Widget ayarları güncellenirken hata:", error);
                this.$message.error("Widget ayarları güncellenirken bir hata oluştu");
            }
        },
        async handleWidgetUpdated(updatedWidget) {
            this.$emit("widget-updated", updatedWidget);
        },
        async handleWidgetDuplicated(result) {
            this.$emit("widget-duplicated", result);
        },
        async handleWidgetDeleted(widgetId) {
            this.$emit("delete", widgetId);
        },
    },
    watch: {
        "widget.settings": {
            handler() {
                this.startTimer();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.countdown-widget {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    border: 2px solid #fafafa;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: #1890ff;

        .drag-handle {
            visibility: visible;
            line-height: 1px;
        }
    }

    .drag-handle {
        transform: translateY(-50%);
        font-size: 20px;
        color: rgba(0, 0, 0, 0.25);
        cursor: move;
        transition: opacity 0.2s, color 0.2s, left 0.2s;
        visibility: hidden;
        line-height: 1px;
        &:hover {
            color: rgba(0, 0, 0, 0.45);
            left: -8px;
        }
    }

    .widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;
        .left {
            display: flex;
            align-items: center;
            gap: 5px;
            width: -webkit-fill-available;
        }
    }

    .widget-content {
        flex: 1;
        min-height: 0;
        position: relative;
        padding: 24px;

        .countdown-content {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 24px;
            height: 100%;

            .countdown-unit {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;

                .value {
                    font-weight: 600;
                }

                .label {
                    color: rgba(0, 0, 0, 0.65);
                }
            }
        }
    }
}
</style> 